import { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { Container, Header, Segment, Button, Message, Label, Input, Divider, Icon, Dropdown } from 'semantic-ui-react';
import { Navigate } from 'react-router-dom';

import { ResponsiveContainer } from '../../../components/ResponsiveContainer';
import { PATHS } from '../../../paths';
import { StreamAccess } from './StreamAccess';
import { Supporters } from './Supporters';
import { Replicators } from './Replicators';

/**
 * Phase 6 — streams.
 *
 * A stream is a channel of posts belonging to the organisation that publishes into it. This page
 * is where they are created, named, and have their readers decided.
 *
 * **Gated on reaching a stream at all**, not on holding a role. `streams` returns exactly what
 * the caller can reach at VIEW, so the page and the data agree by construction — the same
 * approach the gallery page took after 5.9, and for the same reason: a role-named gate drifts
 * from the permission it stands for.
 *
 * Content reviewers manage every stream (`STREAM: MANAGE` from their role) and can create them
 * (`CREATE_STREAM`). Organisation administrators can too. Everybody else sees the streams they
 * have been given and no controls.
 */

const GET_STREAMS = gql`
  query Streams {
    canCreateStream
    streams {
      id name description isDefault postCount myLevel canManage
    }
  }
`;

const CREATE_STREAM = gql`
  mutation CreateStream($name: String!, $description: String, $copyAccessFromStreamId: Int) {
    createStream(name: $name, description: $description, copyAccessFromStreamId: $copyAccessFromStreamId) { id }
  }
`;

const UPDATE_STREAM = gql`
  mutation UpdateStream($streamId: Int!, $name: String, $description: String) {
    updateStream(streamId: $streamId, name: $name, description: $description)
  }
`;

const ARCHIVE_STREAM = gql`
  mutation ArchiveStream($streamId: Int!) {
    archiveStream(streamId: $streamId)
  }
`;

const StreamsView = () => {
  const { data, loading, error, refetch } = useQuery(GET_STREAMS, { fetchPolicy: 'network-only' });
  const [createStream, { loading: creating }] = useMutation(CREATE_STREAM);
  const [updateStream] = useMutation(UPDATE_STREAM);
  const [archiveStream] = useMutation(ARCHIVE_STREAM);

  const [newName, setNewName] = useState<string | null>(null);
  const [copyFrom, setCopyFrom] = useState<number | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [managing, setManaging] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  if (loading) {
    return <ResponsiveContainer><Container><p>Loading…</p></Container></ResponsiveContainer>;
  }

  // Waiting on the query matters: an empty list and an unanswered one look identical, so
  // redirecting on the result alone would bounce somebody mid-load.
  if (error || (data?.streams ?? []).length === 0) {
    return <Navigate to={PATHS.CLASSES} replace />;
  }

  const streams = data.streams;

  return (
    <ResponsiveContainer>
      <Container style={{ paddingBottom: '3em' }}>
        <Header as="h1">Streams</Header>
        <p style={{ color: '#767676' }}>
          Where posts are published, and who can read them.
        </p>

        {actionError && <Message negative onDismiss={() => setActionError(null)}>{actionError}</Message>}

        {data.canCreateStream && (
          <Segment>
            {newName == null
              ? <Button size="small" onClick={() => setNewName('')}>New stream</Button>
              : (
                <div>
                  <Input
                    placeholder="What is it called?"
                    value={newName}
                    style={{ marginRight: '0.5em' }}
                    onChange={(e) => setNewName(e.target.value)}
                  />
                  {/* Duplicating copies **who can read it**, never the posts. A post lives in
                      one stream: two copies would be read twice, engaged with twice, and
                      moderated in one place only. The access list is the tedious part. */}
                  <Dropdown
                    selection
                    clearable
                    placeholder="Start empty"
                    style={{ marginRight: '0.5em', minWidth: '15em' }}
                    value={copyFrom ?? undefined}
                    options={streams.map((s: any) => ({
                      key: s.id, text: `Same readers as ${s.name}`, value: s.id,
                    }))}
                    onChange={(_: any, d: any) => setCopyFrom((d.value as number) || null)}
                  />
                  <Button
                    primary
                    loading={creating}
                    disabled={creating || newName.trim() === ''}
                    onClick={async () => {
                      setActionError(null);
                      try {
                        await createStream({
                          variables: {
                            name: newName,
                            description: null,
                            copyAccessFromStreamId: copyFrom,
                          },
                        });
                        setNewName(null);
                        setCopyFrom(null);
                        await refetch();
                      } catch (e: any) {
                        setActionError(e?.message ?? 'Could not create that stream.');
                      }
                    }}
                  >
                    Create
                  </Button>
                  <Button onClick={() => { setNewName(null); setCopyFrom(null); }}>Cancel</Button>
                  <p style={{ color: '#767676', fontSize: '0.9em', marginTop: '0.5em' }}>
                    It starts with no posts. You will be able to manage it; nobody else sees it
                    until you say so.
                  </p>
                </div>
              )}
          </Segment>
        )}

        {streams.map((s: any) => (
          <Segment key={s.id}>
            <Header as="h3" style={{ marginBottom: '0.2em' }}>
              {editing === s.id
                ? (
                  <Input
                    size="small"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                  />
                )
                : s.name}
              {s.isDefault && <Label size="tiny" style={{ marginLeft: '0.6em' }}>default</Label>}
              {s.myLevel && s.myLevel !== 'MANAGE' && (
                <Label size="tiny" color="grey" style={{ marginLeft: '0.4em' }}>
                  you can {s.myLevel.toLowerCase()}
                </Label>
              )}
            </Header>

            {s.description && <p style={{ color: '#767676' }}>{s.description}</p>}
            <p style={{ color: '#767676', fontSize: '0.9em' }}>
              {s.postCount} post{s.postCount === 1 ? '' : 's'}
            </p>

            {s.canManage && (
              <div>
                {editing === s.id
                  ? (
                    <>
                      <Button
                        size="small"
                        primary
                        onClick={async () => {
                          setActionError(null);
                          try {
                            await updateStream({ variables: { streamId: s.id, name: editName } });
                            setEditing(null);
                            await refetch();
                          } catch (e: any) {
                            setActionError(e?.message ?? 'Could not rename that stream.');
                          }
                        }}
                      >
                        Save
                      </Button>
                      <Button size="small" onClick={() => setEditing(null)}>Cancel</Button>
                    </>
                  )
                  : (
                    <>
                      <Button size="small" onClick={() => { setEditing(s.id); setEditName(s.name); }}>
                        Rename
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setManaging(managing === s.id ? null : s.id)}
                      >
                        <Icon name="users" /> Who can see it
                      </Button>
                      {/* The default stream is where posts go when nothing else decides, so it
                          cannot be archived — the server refuses too. */}
                      {!s.isDefault && s.postCount === 0 && (
                        <Button
                          size="small"
                          onClick={async () => {
                            setActionError(null);
                            try {
                              await archiveStream({ variables: { streamId: s.id } });
                              await refetch();
                            } catch (e: any) {
                              setActionError(e?.message ?? 'Could not archive that stream.');
                            }
                          }}
                        >
                          Archive
                        </Button>
                      )}
                    </>
                  )}
              </div>
            )}

            {managing === s.id && (
              <StreamAccess
                streamId={s.id}
                streamName={s.name}
                onError={setActionError}
              />
            )}
          </Segment>
        ))}

        <Replicators />

        <Supporters />

        <Divider />
        <p style={{ color: '#767676', fontSize: '0.9em' }}>
          Giving somebody <em>view</em> lets them read a stream's posts; <em>contribute</em> lets
          them post to it; <em>manage</em> lets them rename it and decide who else can see it.
        </p>
      </Container>
    </ResponsiveContainer>
  );
};

export { StreamsView };
